import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { IntakeQuestion, IntakeSection, IntakeTab } from '../../types/aai';
import { Loading } from '../aa';
import { OrbitDeck } from '../orbit-deck/OrbitDeck';
import type { DeckAnswer, DeckCardModel, DeckInput, DeckOption } from '../orbit-deck/types';
import { transcribeFor } from '../../pages/orbit-deck/deckModel';
import { refreshCompanion } from '../companion/AiCompanion';

/*
 * The intake this member answers, tab by tab: their own person questions
 * first, then the opening, business, department and tool questions meant for
 * their role, dealt as cards. What Orbit already knows arrives filled in, to
 * confirm or change; only what nobody knows is asked from scratch.
 */

const TAB_LABEL: Record<IntakeTab, string> = {
  opening: 'Opening questions',
  business: 'Business intake',
  department: 'Department intake',
  person: 'Person intake',
  tool: 'Tool intake',
};

const POLL_MS = 4000;
const POLL_TIMES = 6;

const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
};

const keyOf = (s: IntakeSection): string => s.key || s.id;

const titleOf = (s: IntakeSection): string => {
  if (s.subject_label) return `${s.title} · ${s.subject_label}`;
  return s.title;
};

const INPUT_OF: Record<string, DeckInput> = { text: 'say', one: 'choose', many: 'choose_many', number: 'number' };

const mustAnswer = (s: IntakeSection, q: IntakeQuestion): boolean => s.intake === 'opening' || !q.optional;

const optionsFor = (q: IntakeQuestion, prefill: string): DeckOption[] | undefined => {
  if (q.kind !== 'one' && q.kind !== 'many') return undefined;
  const chosen = prefill.split(',').map((v) => v.trim().toLowerCase()).filter(Boolean);
  const out: DeckOption[] = (q.options || []).map((o) => ({ value: o, label: o, detected: chosen.includes(o.toLowerCase()) }));
  if (q.kind === 'many') out.push({ value: 'other', label: 'Other' });
  return out;
};

type Flat = { section: IntakeSection; q: IntakeQuestion; answer: string; filled: string };

/* A row on the page: one section, or every tool's copy of a tool section
   together, so the person answers all their tools in one place. */
type Row = { key: string; title: string; first: IntakeSection; entries: IntakeSection[] };

const rowsOf = (sections: IntakeSection[]): Row[] => {
  const rows: Row[] = [];
  const toolRows: Record<string, Row> = {};
  sections.forEach((s) => {
    if (s.repeat !== 'tool' || !s.subject) {
      rows.push({ key: keyOf(s), title: titleOf(s), first: s, entries: [s] });
      return;
    }
    const existing = toolRows[s.id];
    if (existing) {
      existing.entries.push(s);
      return;
    }
    toolRows[s.id] = { key: `${s.id}:tools`, title: s.title, first: s, entries: [s] };
    rows.push(toolRows[s.id]);
  });
  return rows;
};

const cardFor = (f: Flat, round: number): DeckCardModel => {
  const title = titleOf(f.section);
  let tag: DeckCardModel['tag'] = 'ask';
  let tagLabel = title;
  let why = f.section.description;
  if (f.filled) {
    tag = 'confirm';
    tagLabel = `Orbit filled this · ${title}`;
    why = f.q.filled_note || 'Orbit filled this from what you told us. Check it, and change it if it is wrong.';
  }
  if (f.answer) {
    tag = 'confirm';
    tagLabel = `Your answer · ${title}`;
  }
  const prefill = f.answer || f.filled;
  const input = INPUT_OF[f.q.kind || 'text'] || 'say';
  const card: DeckCardModel = {
    id: `${keyOf(f.section)}:${f.q.id}:${round}`,
    tag,
    tagLabel,
    title: f.q.label,
    why,
    input,
    options: optionsFor(f.q, prefill),
    placeholder: 'Your answer',
    prefill: prefill || undefined,
    fieldKey: f.q.id,
    meta: { section: f.section.id, subject: f.section.subject || '' },
  };
  if (f.filled && (f.q.kind === 'one' || f.q.kind === 'many')) card.detectedNote = 'Orbit picked these from what you told us. Tap to change.';
  if (!f.answer && mustAnswer(f.section, f.q)) {
    card.noSkip = true;
    card.footNote = 'This one needs an answer.';
  }
  return card;
};

/* One row's cards, as two decks: what is still to answer (Orbit's filled
   ones first, to confirm), and what they already answered. */
const SectionDeck: React.FC<{ clientId: string; row: Row; onSaved: (next: IntakeSection[]) => void }> = ({ clientId, row, onSaved }) => {
  const flat: Flat[] = row.entries.flatMap((section) => section.questions.map((q) => ({
    section, q, answer: (q.answer || '').trim(), filled: (q.filled || '').trim(),
  })));
  const heldCount = flat.filter((f) => f.answer).length;
  const openCount = flat.length - heldCount;
  const [shown, setShown] = useState<'held' | 'open'>(() => {
    if (openCount > 0) return 'open';
    return 'held';
  });
  const [round, setRound] = useState(0);
  const [through, setThrough] = useState(false);
  const transcribe = useMemo(() => transcribeFor(clientId), [clientId]);

  /* Dealt once per round, so saving a card does not reshuffle the deck under them. */
  const cards = useMemo(() => {
    if (shown === 'held') return flat.filter((f) => f.answer).map((f) => cardFor(f, round));
    const open = flat.filter((f) => !f.answer);
    const filledFirst = [...open.filter((f) => f.filled), ...open.filter((f) => !f.filled)];
    return filledFirst.map((f) => cardFor(f, round));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [shown, round]);

  const answer = async (card: DeckCardModel, value: DeckAnswer) => {
    const section = row.entries.find((s) => s.id === card.meta?.section && (s.subject || '') === card.meta?.subject) || row.first;
    let text = '';
    if (Array.isArray(value.value)) text = (value.value as unknown[]).join(', ');
    if (!Array.isArray(value.value)) text = String(value.value ?? '');
    try {
      const r = await aaiApi.answerMyIntake(section.id, { [card.fieldKey || '']: text }, clientId, section.subject || '');
      onSaved(r.data.sections);
      refreshCompanion();
    } catch (err) {
      toast.error(errorText(err, 'Could not save that answer.'));
      throw new Error('answer failed');
    }
  };

  const pick = (next: 'held' | 'open') => { setShown(next); setRound((n) => n + 1); setThrough(false); };

  return (
    <div>
      <div className="mb-4 flex w-max overflow-hidden rounded-full border border-line bg-white p-0.5" role="tablist" aria-label={`${row.title} cards`}>
        {([['open', 'to answer', openCount], ['held', 'answered', heldCount]] as const).map(([id, word, n]) => (
          <button key={id} type="button" role="tab" aria-selected={shown === id} onClick={() => pick(id)}
            className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors ${shown === id ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'}`}>
            <span className="tabular-nums">{n}</span> {word}
          </button>
        ))}
      </div>
      {cards.length > 0 && !through && (
        <OrbitDeck
          key={`${shown}:${round}`}
          cards={cards}
          onAnswer={answer}
          onSkip={async () => undefined}
          onDocument={async () => { toast.info('Here, answer or skip each card.'); return undefined; }}
          onDone={() => setThrough(true)}
          optimistic
          transcribe={transcribe}
          labels={{
            deckTitle: shown === 'held' ? 'Your answers' : 'To answer',
            primary: 'Save',
            skip: shown === 'held' ? 'Leave it as it is' : 'Skip this one',
            skipWord: shown === 'held' ? 'next' : 'skip',
          }}
        />
      )}
      {(cards.length === 0 || through) && (
        <div className="rounded-[14px] border border-line bg-[var(--aa-card)] px-5 py-4 text-[13px] text-ink">
          {shown === 'open' && openCount === 0 && 'Nothing left to answer here. Thank you.'}
          {shown === 'open' && openCount > 0 && `${openCount} still to answer. Skipped ones wait here.`}
          {shown === 'held' && heldCount === 0 && 'Nothing answered yet.'}
          {shown === 'held' && heldCount > 0 && 'That is everything you answered here.'}
          <button type="button" className="ml-2 font-semibold underline" onClick={() => { setRound((n) => n + 1); setThrough(false); }}>
            Go through them again
          </button>
        </div>
      )}
    </div>
  );
};

/* Every section as its own row, collapsed, each saying how much is waiting
   and opening onto its cards. One open at a time. */
export const MemberIntake: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [sections, setSections] = useState<IntakeSection[] | null>(null);
  const [filling, setFilling] = useState(false);
  const [error, setError] = useState('');
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const companionTarget = searchParams.get('companion');
  const targetSection = searchParams.get('section');
  const polls = useRef(0);

  const load = useCallback(async () => {
    try {
      const r = await aaiApi.myIntake(clientId);
      setSections(r.data.sections);
      setFilling(Boolean(r.data.filling));
      const first = rowsOf(r.data.sections).find((row) => row.entries.some((s) => s.questions.some((q) => !(q.answer || '').trim())));
      if (first) setOpenKey((k) => k || first.key);
    } catch (err) {
      setError(errorText(err, 'Could not load your questions.'));
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  /* Orbit fills cards in the background; look again a few times while it works. */
  useEffect(() => {
    if (!filling || polls.current >= POLL_TIMES) return undefined;
    const timer = window.setTimeout(() => { polls.current += 1; load(); }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [filling, sections, load]);

  const loaded = sections !== null;
  useEffect(() => {
    if (!loaded || companionTarget !== 'part') return undefined;
    let anchor = 'member-intake';
    const target = rowsOf(sections || []).find((row) => row.entries.some((s) => s.id === targetSection));
    if (target) {
      setOpenKey(target.key);
      anchor = `intake-section-${target.key}`;
    }
    const timer = window.setTimeout(() => {
      document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, companionTarget, targetSection]);

  if (error) return <div className="text-[13px] text-danger">{error}</div>;
  if (!sections) return <Loading label="Loading your questions" />;

  return (
    <div className="path scroll-mt-20" id="member-intake">
      {filling && <p className="m-0 mb-3 text-[12.5px] text-ink-dim">Orbit is filling in what it already knows. Those cards arrive ready for you to check.</p>}
      {rowsOf(sections).map((row, i) => {
        const s = row.first;
        const questions = row.entries.flatMap((e) => e.questions);
        const held = questions.filter((q) => (q.answer || '').trim()).length;
        const filled = questions.filter((q) => !(q.answer || '').trim() && (q.filled || '').trim()).length;
        const open = questions.length - held;
        const key = row.key;
        const shown = openKey === key;
        let state = '';
        let side = 'Nothing yet';
        if (open === 0) { state = 'done'; side = 'Answered'; }
        if (open > 0) { state = 'now'; side = `${open} to answer`; }
        const line = [
          open - filled > 0 ? `${open - filled} to answer` : '',
          filled > 0 ? `${filled} filled by Orbit, to check` : '',
          held > 0 ? `${held} answered` : '',
        ].filter(Boolean).join(' · ');
        let who = `${TAB_LABEL[s.intake || 'person']} · added by your partner`;
        if (s.fixed) who = 'From Agents Anywhere';
        if (s.intake === 'opening') who = 'Must be answered';
        if (row.entries.length > 1) who = `${who} · ${row.entries.map((e) => e.subject_label || e.subject).join(', ')}`;
        return (
          <div key={key} id={`intake-section-${key}`} className="scroll-mt-20">
            <button
              type="button"
              aria-expanded={shown}
              onClick={() => setOpenKey(shown ? null : key)}
              className={`phase ${state} w-full cursor-pointer text-left transition-shadow hover:shadow-sm`}
            >
              <span className="n">{state === 'done' ? '✓' : i + 1}</span>
              <div>
                <strong>{row.title}</strong>
                <span className="who">{who}</span>
                <span className="prog">{line}</span>
              </div>
              <div className="side">
                <em>{side}</em>
                <span className="inline-flex items-center gap-1 rounded-full border border-line-strong bg-white px-3 py-1.5 text-[11.5px] font-semibold text-ink">
                  {shown ? 'Close' : 'Open'}
                  {shown ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </span>
              </div>
            </button>
            {shown && (
              <div className="mb-2 mt-3 pl-0 sm:pl-[50px]">
                <SectionDeck key={`${key}:${filled}`} clientId={clientId} row={row} onSaved={setSections} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MemberIntake;
